/**
 * The whole plan turned about its middle, the app's plan rotation (LevelController.DomainRotation → RotateLevelAbout):
 * every floor about one centre — the area-weighted middle of all the rooms of the building — as one change. What
 * stands on the plan turns with it: the walls' ends and axes, the rooms' and the floor's outlines, where every piece
 * stands and which way it faces, the saved camera; the floors' and ceilings' finishes turn with their rooms
 * (RotatePlainMaterial), so a parquet keeps its run along the room. Nothing records the turn: the turned coordinates
 * are the plan, as the app keeps them. Angles here run as a wall's `theta` does: from +x towards +z.
 */
import type { Item, Level, Room, Scene, Surface, Vec2, Wall } from '../core/hp/types';
import { roomsBesideWall } from '../core/geometry/roomFloor';
import { surfaceRepeat } from '../core/hp/surfaceTransform';

/** How near a right angle the plan's bearing is held to it (the app's MagnetRange), in degrees. */
export const PLAN_ROTATION_MAGNET = 3;

const r4 = (value: number) => Math.round(value * 1e4) / 1e4;
/** An angle in degrees brought into (−180, 180]. */
const degrees = (value: number) => { let d = value % 360; if (d <= -180) d += 360; else if (d > 180) d -= 360; return Math.abs(d) < 1e-9 ? 0 : d; };
/** An angle in radians brought into (−π, π]. */
const radians = (value: number) => { let r = value % (2 * Math.PI); if (r <= -Math.PI) r += 2 * Math.PI; else if (r > Math.PI) r -= 2 * Math.PI; return r; };

/** The middle the building turns about: its rooms' centroid weighted by their areas over every floor, else the walls' middle. */
export function planRotationCentre(scene: Pick<Scene, 'levels'>): Vec2 {
  let area = 0, x = 0, z = 0;
  for (const level of scene.levels) for (const room of level.rooms) {
    const p = room.polygon;
    for (let i = 0; i < p.length; i++) {
      const a = p[i], b = p[(i + 1) % p.length], cross = a.x * b.z - b.x * a.z;
      area += cross; x += (a.x + b.x) * cross; z += (a.z + b.z) * cross;
    }
  }
  if (Math.abs(area) > 1e-6) return { x: r4(x / (3 * area)), z: r4(z / (3 * area)) };
  const points = scene.levels.flatMap(level => level.walls.flatMap(wall => [wall.start, wall.end]));
  if (!points.length) return { x: 0, z: 0 };
  const xs = points.map(point => point.x), zs = points.map(point => point.z);
  return { x: r4((Math.min(...xs) + Math.max(...xs)) / 2), z: r4((Math.min(...zs) + Math.max(...zs)) / 2) };
}

/** The turn's cosine and sine, exact on a quarter turn so a square plan stays square to the tenth of a millimetre. */
function turnOf(angle: number): { cos: number; sin: number } {
  const quarters = Math.round(angle / (Math.PI / 2));
  if (Math.abs(angle - quarters * Math.PI / 2) < 1e-9) {
    const q = ((quarters % 4) + 4) % 4;
    return { cos: [1, 0, -1, 0][q], sin: [0, 1, 0, -1][q] };
  }
  return { cos: Math.cos(angle), sin: Math.sin(angle) };
}

/**
 * A floor's or a ceiling's finish turned with its room: its UVs are the room's own metres (x, −z) through the texture's
 * rotation, scale and offset, so the rotation takes the turn and the offset what the turn about `centre` moves.
 */
export function turnedPlainSurface(surface: Surface | undefined, centre: Vec2, angle: number): Surface | undefined {
  if (!surface || !(surface.texture || surface.customTexture)) return surface;
  const [sx, sy] = surfaceRepeat(surface), theta = (surface.rotation ?? 0) * Math.PI / 180;
  const at = (t: number) => ({ u: centre.x * Math.cos(t) + centre.z * Math.sin(t), v: centre.x * Math.sin(t) - centre.z * Math.cos(t) });
  const before = at(theta), after = at(theta + angle), offset = surface.offset ?? [0, 0];
  return { ...surface, rotation: r4(degrees((surface.rotation ?? 0) + angle * 180 / Math.PI)),
    offset: [r4(offset[0] + sx * (before.u - after.u)), r4(offset[1] + sy * (before.v - after.v))] };
}

/** `scene` with every floor turned by `angle` radians about `centre`, as one change of the scene. */
export function rotateScene(scene: Scene, angle: number, centre: Vec2): Scene {
  if (!Number.isFinite(angle) || Math.abs(angle) < 1e-9) return scene;
  const { cos, sin } = turnOf(angle);
  const at = (p: Vec2): Vec2 => ({ x: r4(centre.x + (p.x - centre.x) * cos - (p.z - centre.z) * sin), z: r4(centre.z + (p.x - centre.x) * sin + (p.z - centre.z) * cos) });
  const along = (v: Vec2): Vec2 => ({ x: Math.round((v.x * cos - v.z * sin) * 1e9) / 1e9, z: Math.round((v.x * sin + v.z * cos) * 1e9) / 1e9 });
  const wall = (w: Wall): Wall => {
    const start = at(w.start), end = at(w.end);
    return { ...w, start, end, center: { x: r4((start.x + end.x) / 2), z: r4((start.z + end.z) / 2) }, length: r4(Math.hypot(end.x - start.x, end.z - start.z)),
      dir: along(w.dir), normalIn: along(w.normalIn), theta: r4(degrees(w.theta + angle * 180 / Math.PI)) };
  };
  const room = (r: Room): Room => ({ ...r, polygon: r.polygon.map(at), floorSurface: turnedPlainSurface(r.floorSurface, centre, angle), ceilingSurface: turnedPlainSurface(r.ceilingSurface, centre, angle) });
  // A piece's yaw grows with the walls' theta, as a piece on a turned wall turns with it (rebuildWalls).
  const item = (i: Item): Item => ({ ...i, position: at(i.position), rotation: r4(radians(i.rotation + angle)) });
  const level = (l: Level): Level => ({ ...l, outline: l.outline.map(at), walls: l.walls.map(wall), rooms: l.rooms.map(room), items: l.items.map(item) });
  // The saved camera looks along (sin yaw, cos yaw): turning that heading with the plan takes the turn off its yaw.
  const camera = scene.camera && { ...scene.camera, position: at(scene.camera.position), yaw: r4(radians(scene.camera.yaw - angle)) };
  return { ...scene, levels: scene.levels.map(level), ...(camera ? { camera } : {}) };
}

/**
 * Where the plan's handle stands off the floor, as the app's PlanRotationAnchor: past the floor's longest outer wall —
 * a room on one side of it only — on the ray from `centre` along that wall's outward normal, whose angle is the plan's
 * bearing the right-angle magnet holds; `reach` how far along the ray the wall's outer face is. `keep` holds the wall
 * it stood off before while it is still an outer one, so a square room's handle does not hop between its equal sides;
 * of sides as long as the longest within 5 %, the one facing `prefer` most (the screen's bottom, clear of the header).
 * None on a floor without an outer wall.
 */
export function planRotationAnchor(level: Pick<Level, 'walls' | 'rooms'>, centre: Vec2, keep?: string, prefer: Vec2 = { x: 0, z: 1 }): { wall: Wall; outward: Vec2; bearing: number; reach: number } | null {
  const outer: { wall: Wall; outward: Vec2 }[] = [];
  for (const wall of level.walls) {
    if (wall.hidden || wall.length < .05) continue;
    const sides = roomsBesideWall(level, wall);
    if (sides.front === sides.back) continue;
    outer.push({ wall, outward: sides.front ? { x: -wall.normalIn.x, z: -wall.normalIn.z } : { ...wall.normalIn } });
  }
  const longest = Math.max(0, ...outer.map(candidate => candidate.wall.length));
  const facing = (candidate: { outward: Vec2 }) => candidate.outward.x * prefer.x + candidate.outward.z * prefer.z;
  const best = outer.find(candidate => candidate.wall.id === keep)
    ?? outer.filter(candidate => candidate.wall.length >= longest * .95).sort((a, b) => facing(b) - facing(a))[0] ?? null;
  if (!best) return null;
  const face = { x: best.wall.center.x + best.outward.x * best.wall.thickness / 2, z: best.wall.center.z + best.outward.z * best.wall.thickness / 2 };
  const reach = (face.x - centre.x) * best.outward.x + (face.z - centre.z) * best.outward.z;
  const radius = Math.max(...level.walls.flatMap(wall => [wall.start, wall.end]).map(point => Math.hypot(point.x - centre.x, point.z - centre.z)));
  return { ...best, bearing: Math.atan2(best.outward.z, best.outward.x), reach: reach > .05 ? reach : radius };
}

/**
 * The turn a drag asks for, held by the right-angle magnet: when the bearing it leads to is within PLAN_ROTATION_MAGNET
 * degrees of a quarter turn — measured absolutely, so a plan saved crooked can be squared again — it goes there.
 */
export function heldTurn(bearing: number, turn: number, free = false): number {
  if (free) return turn;
  const target = (bearing + turn) * 180 / Math.PI, square = Math.round(target / 90) * 90;
  return Math.abs(target - square) <= PLAN_ROTATION_MAGNET ? turn + (square - target) * Math.PI / 180 : turn;
}

/** The bearing shown beside the handle while it turns, in whole degrees from 0 to 359. */
export const bearingDegrees = (bearing: number) => ((Math.round(bearing * 180 / Math.PI) % 360) + 360) % 360;
